/**
 * WeekView — Week-level time entry form.
 * Extracted from TimeTracking.jsx for maintainability.
 */
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { ChevronLeft, ChevronRight, Save, FileDown, Copy } from 'lucide-react';

const DAYS_FULL = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];

export const WeekView = ({
  weekDates,
  weekData,
  setWeekData,
  weekRef,
  setWeekRef,
  weekTotal,
  saving,
  handleSaveWeek,
  applyContractSchedule,
  copyPreviousWeek,
  selectedContract,
  getContractName,
  getSelectedContract,
  getHolidayName,
  getTodayStr,
  parseLocalDate,
  formatWeekLabel,
}) => {
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Button
                variant="outline" size="icon" className="h-8 w-8 shrink-0"
                onClick={() => { const d = new Date(weekRef); d.setDate(d.getDate() - 7); setWeekRef(d); }}
                data-testid="week-prev"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm font-semibold text-center whitespace-nowrap" data-testid="week-label">
                {formatWeekLabel(weekDates)}
              </span>
              <Button
                variant="outline" size="icon" className="h-8 w-8 shrink-0"
                onClick={() => { const d = new Date(weekRef); d.setDate(d.getDate() + 7); setWeekRef(d); }}
                data-testid="week-next"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
            <Badge variant="outline" className="text-sm font-mono px-2 py-1 shrink-0" data-testid="week-total">
              {weekTotal.toFixed(1)}h
            </Badge>
          </div>
          <div className="flex justify-end">
            <Button
              onClick={handleSaveWeek}
              disabled={saving}
              style={{ backgroundColor: 'var(--primary-400)' }}
              size="sm"
              className="w-full sm:w-auto"
              data-testid="save-week-btn"
            >
              <Save className="h-4 w-4 mr-1" />
              {saving ? 'Enregistrement...' : 'Enregistrer la semaine'}
            </Button>
          </div>
        </div>
        <CardDescription>
          Saisissez les horaires ou le nombre d'heures pour chaque jour ({getContractName(selectedContract)})
        </CardDescription>
        <div className="flex flex-wrap gap-2 mt-2">
          <Button variant="outline" size="sm" onClick={applyContractSchedule} className="text-xs" data-testid="apply-schedule-btn">
            <FileDown className="h-3.5 w-3.5 mr-1" /> Appliquer horaires du contrat
          </Button>
          <Button variant="outline" size="sm" onClick={copyPreviousWeek} className="text-xs" data-testid="copy-prev-week-btn">
            <Copy className="h-3.5 w-3.5 mr-1" /> Copier semaine précédente
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          <div className="hidden md:grid grid-cols-[120px_1fr_1fr_100px] gap-3 text-xs font-semibold text-[var(--text-secondary)] px-2">
            <span>Jour</span><span>Début</span><span>Fin</span><span>Heures</span>
          </div>

          {weekDates.map((dateStr, idx) => {
            const data = weekData[dateStr] || { start: '', end: '', hours: '' };
            const isWeekend = idx >= 5;
            const isToday = dateStr === getTodayStr();
            const holidayName = getHolidayName(dateStr);
            const contract = getSelectedContract();
            const canWorkHoliday = contract?.work_on_holidays === true;
            const isBlockedHoliday = holidayName && !canWorkHoliday;
            const isLeavePending = data.is_leave_pending;

            return (
              <div
                key={dateStr}
                className={cn(
                  "p-2.5 rounded-lg border transition-all duration-150",
                  holidayName && !data.is_absent && !isLeavePending && "bg-[#E17A73]/8 border-[#E17A73]/40",
                  data.is_absent && "bg-[var(--warning)]/10 border-[var(--warning)]",
                  isLeavePending && !data.is_absent && "bg-[var(--warning)]/5 border-[var(--warning)]/40 border-dashed",
                  isToday && !data.is_absent && !holidayName && !isLeavePending && "bg-[var(--primary-50)] border-[var(--primary-300)]",
                  isWeekend && !isToday && !data.is_absent && !holidayName && !isLeavePending && "bg-[var(--neutral-50)]",
                  !isToday && !isWeekend && !data.is_absent && !holidayName && !isLeavePending && "border-[var(--neutral-200)]"
                )}
                data-testid={`week-row-${idx}`}
              >
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  <span className={cn("text-sm font-semibold", isToday ? "text-[var(--primary-400)]" : "text-[var(--text-primary)]")}>
                    {DAYS_FULL[idx]}
                  </span>
                  <span className="text-xs text-[var(--text-tertiary)]">
                    {parseLocalDate(dateStr).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                  </span>
                  {holidayName && (
                    <Badge variant="outline" className="bg-[#E17A73]/10 text-[#E17A73] border-[#E17A73]/40 text-[10px] px-1.5 py-0" data-testid={`week-holiday-${idx}`}>
                      {holidayName}
                    </Badge>
                  )}
                  {data.is_absent && (
                    <Badge variant="outline" className="bg-[var(--warning)]/10 text-[var(--warning)] border-[var(--warning)] text-xs">
                      {data.absence_type || 'Absent'}
                    </Badge>
                  )}
                  {isLeavePending && !data.is_absent && (
                    <Badge variant="outline" className="bg-[var(--warning)]/5 text-[var(--warning)]/70 border-[var(--warning)]/40 border-dashed text-xs italic">
                      Congé (en attente)
                    </Badge>
                  )}
                </div>

                {data.is_absent ? (
                  <div className="text-sm text-[var(--warning)]">
                    {data.absence_type ? `${data.absence_type}` : "Journée d'absence déclarée"}
                  </div>
                ) : isLeavePending ? (
                  <div className="text-sm text-[var(--warning)]/70 italic">Congé en attente de validation</div>
                ) : isBlockedHoliday ? (
                  <div className="text-sm text-[#E17A73] italic">Jour férié — repos</div>
                ) : (
                  <div className="grid grid-cols-[1fr_1fr_70px] gap-2 items-center">
                    <Input
                      type="time" value={data.start}
                      onChange={(e) => {
                        const newData = { ...weekData };
                        newData[dateStr] = { ...data, start: e.target.value };
                        if (e.target.value && data.end) {
                          const s = e.target.value.split(':').map(Number);
                          const en = data.end.split(':').map(Number);
                          const h = (en[0] * 60 + en[1] - s[0] * 60 - s[1]) / 60;
                          newData[dateStr].hours = h > 0 ? h.toFixed(1) : '';
                        }
                        setWeekData(newData);
                      }}
                      className="h-9 text-sm" placeholder="08:00" data-testid={`week-start-${idx}`}
                    />
                    <Input
                      type="time" value={data.end}
                      onChange={(e) => {
                        const newData = { ...weekData };
                        newData[dateStr] = { ...data, end: e.target.value };
                        if (data.start && e.target.value) {
                          const s = data.start.split(':').map(Number);
                          const en = e.target.value.split(':').map(Number);
                          const h = (en[0] * 60 + en[1] - s[0] * 60 - s[1]) / 60;
                          newData[dateStr].hours = h > 0 ? h.toFixed(1) : '';
                        }
                        setWeekData(newData);
                      }}
                      className="h-9 text-sm" placeholder="18:00" data-testid={`week-end-${idx}`}
                    />
                    <Input
                      type="number" step="0.5" min="0" max="24" value={data.hours}
                      onChange={(e) => {
                        const newData = { ...weekData };
                        newData[dateStr] = { ...data, hours: e.target.value };
                        setWeekData(newData);
                      }}
                      className="h-9 font-mono text-center text-sm" placeholder="0" data-testid={`week-hours-${idx}`}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};
